import { type TRankingOption } from '@hubertai/screening-question-types';
import { IconButton } from '@radix-ui/themes';
import HubertInput from 'components/HubertUI/HubertInput';
import HubertSwitch from 'components/HubertUI/HubertSwitch';
import { useInTemplateEditAndUneditable } from 'hooks/useInTemplateEditAndUneditable';
import { Trash2Icon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'rtk/query/hooks';
import { addOption, removeOptionById, toggleOptionEnabled, updateOption } from 'rtk/Question.Editor.Slice';
import {
  selectQuestionIsPublishedOrNonExclusive,
  selectQuestionIsReadonly,
  selectQuestionLanguage,
  selectQuestionMode,
  selectQuestionOptions,
} from 'rtk/questionEditorSelector';
import { ECreateQuestionMode, useQuestionEditorContext } from '../../context/create-question.context';
import UneditableInfoTooltip from '../../UneditableInfoTooltip';
import { useTemplateId } from 'hooks/useTemplate';
import { updateInTemplateOptionEnabled } from 'rtk/questionEditorThunks';
import { useInRecruiterApp } from 'hooks/useInRecruiterApp';
import ClickToCopyButton from 'components/common/ClickToCopyButton';
import { useTranslation } from 'react-i18next';

const CreateRankingOption = ({
  data,
}: {
  data: {
    item: TRankingOption;
    index: number;
  };
}) => {
  const { t } = useTranslation('Questionsv2');
  const dispatch = useAppDispatch();
  const { questionId } = useQuestionEditorContext();
  const questionLanguage = useAppSelector((state) => selectQuestionLanguage(state, questionId));
  const mode = useAppSelector((state) => selectQuestionMode(state, questionId));
  const isReadOnly = useAppSelector((state) => selectQuestionIsReadonly(state, questionId));
  const options = useAppSelector((state) => selectQuestionOptions(state, questionId));

  const inTemplateEditAndUneditable = useInTemplateEditAndUneditable(questionId, mode);
  const disabled = mode === ECreateQuestionMode.InTemplateEdit;
  const [label, setLabel] = useState(data.item.label[questionLanguage] || '');
  const templateId = useTemplateId();
  const showIfNotInRecruiter = !useInRecruiterApp();
  const isUneditable = useAppSelector((state) => selectQuestionIsPublishedOrNonExclusive(state, questionId));

  useEffect(() => {
    setLabel(data.item.label[questionLanguage] || '');
  }, [questionLanguage, data.item.label]);

  return (
    <div className="hover:bg-neutral-purple flex w-full items-center justify-between rounded-md pt-2 pr-3 pb-2 pl-3">
      <UneditableInfoTooltip disabled={inTemplateEditAndUneditable}>
        <div className="flex w-full min-w-0 flex-col">
          {showIfNotInRecruiter ? (
            <ClickToCopyButton
              text={t('ranking.optionId', { id: data.item.id })}
              copyText={String(data.item.id)}
              className="my-0.5 py-0 text-sm"
            />
          ) : null}
          <HubertInput
            placeholder={t('ranking.optionPlaceholder')}
            tabIndex={0}
            type="text"
            dir={'auto'}
            disabled={disabled}
            value={label}
            onChange={(e) => {
              setLabel(e.target.value);
              if (data) {
                dispatch(
                  updateOption({
                    questionId,
                    language: questionLanguage,
                    index: data.index,
                    field: 'label',
                    value: e.target.value,
                  }),
                );
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && e.shiftKey) {
                e.preventDefault();
                dispatch(addOption({ questionId, language: questionLanguage }));
                return;
              }
            }}
            className="w-full"
          />
        </div>
      </UneditableInfoTooltip>

      <div className={`${showIfNotInRecruiter ? 'mt-5' : ''} ml-2 flex items-center justify-end gap-2`}>
        <UneditableInfoTooltip disabled={inTemplateEditAndUneditable}>
          <HubertSwitch
            checked={data.item.enabled[questionLanguage]}
            disabled={isReadOnly}
            onCheckedChange={(value) => {
              dispatch(toggleOptionEnabled({ questionId, language: questionLanguage, index: data.index, value }));
              const optionId = options[data.index].id;
              updateInTemplateOptionEnabled(templateId, optionId, questionId, questionLanguage, dispatch, mode, value);
            }}
          />
        </UneditableInfoTooltip>

        <UneditableInfoTooltip disabled={inTemplateEditAndUneditable}>
          <IconButton
            variant="ghost"
            color="red"
            className={isUneditable ? '' : 'cursor-pointer!'}
            disabled={isUneditable}
            onClick={() => dispatch(removeOptionById({ questionId, id: data.item.id }))}
          >
            <Trash2Icon />
          </IconButton>
        </UneditableInfoTooltip>
      </div>
    </div>
  );
};

export default CreateRankingOption;
